import PaymentCalculationService from '@/modules/loanpro/services/payment-calculation.service';

/**
 * LoanManagementService
 *
 */
export default class LoanManagementService {
  constructor({
    insuranceFloor = 0,
    freeInsurance = 0,
    needDisbursementSchedule = false
  } = {}) {
    this._insuranceFloor = insuranceFloor ?? 0;
    this._freeInsurance = freeInsurance ?? 0;
    this._needDisbursementSchedule = needDisbursementSchedule;
  }

  /**
   * Returns a total loan amount based on a sum of the total
   * internal, external, consolidated and rollup amount.
   *
   * @param { Object } loan - loan details object
   * @returns { Number }
   */
  static calculateTotalLoanAmount(loan) {
    return (
      +(loan.totalInternalLoanAmount || 0) +
      +(loan.totalExternalLoanAmount || 0) +
      +(loan.totalConsolidatedAmount || 0) +
      +(loan.totalRollUp || 0)
    );
  }

  /**
   * Returns a total internal loan amount based on a sum of the total
   * internal, consolidated and rollup amount.
   *
   * @param { Object } loan - loan details object
   * @returns { Number }
   */
  static calculateTotalInternalLoanAmount(loan) {
    return (
      +(loan.totalInternalLoanAmount || 0) +
      +(loan.totalConsolidatedAmount || 0) +
      +(loan.totalRollUp || 0)
    );
  }

  /**
   * Returns a list of actions to be carried out on the loan
   *
   * @param { Object } loan - loan details object
   * @returns Array<{
   *  id: number,
   *  name: string,
   *  type: string,
   *  description: string,
   *  icon: string,
   *  isValid: () => boolean,
   *  disabled: () => boolean,
   * }>
   */
  getOutstandingActions(loan) {
    const { totalOutstandingCollateral } =
      LoanManagementService.getCollateralProportions(loan);

    const { totalOutstandingInsurance } =
      LoanManagementService.getLifeInsuranceProportions(loan, {
        freeInsurance: this._freeInsurance,
        insuranceFloor: this._insuranceFloor
      });

    const processingOfficerChecklistItems =
      LoanManagementService.getOutstandingChecklistItems(loan, [
        'processing officer'
      ]);
    const reviewingOfficerChecklistItems =
      LoanManagementService.getOutstandingChecklistItems(loan, [
        'reviewing officer'
      ]);

    const actions = [
      {
        id: 1,
        name: 'Need Waiver',
        type: 'waiver',
        description:
          'One or more loan terms are exceeding the product policy please apply or request waiver',
        icon: 'mdi-check-decagram',
        isValid: () => {
          return Object.keys(loan?.loanTermWaiver || {})?.length == 0;
        }
      },
      {
        id: 2,
        name: 'Add Collateral',
        type: 'collateral',
        description: 'Add collateral amount required by shares and ltv.',
        icon: 'mdi-domain',
        isValid: () => totalOutstandingCollateral <= 0
      },
      {
        id: 3,
        type: 'insurance',
        name: 'Add Life Insurance',
        description: 'Add life insurance required policy.',
        icon: 'mdi-shield-check',
        isValid: () => totalOutstandingInsurance <= 0
      },
      {
        id: 4,
        type: 'processing officer checklist',
        name: "Complete Processing Officer's Checklist",
        description:
          'List of actions to be completed by the processing officer.',
        icon: 'mdi-check-circle-outline',
        isValid: () => {
          return (
            processingOfficerChecklistItems.filter(
              (el) =>
                el.status?.toLowerCase() !== 'done' &&
                el.status?.toLowerCase() !== 'waived'
            ).length == 0
          );
        }
      },
      {
        id: 5,
        type: 'review checklist',
        name: "Complete Reviewing Officer's Checklist",
        icon: 'mdi-check-circle',
        description:
          'List of actions to be completed by the reviewing officer.',
        disabledText:
          "This item will be active after you complete the processing officer's checklist.",
        isValid: function () {
          const isDoneFilter = (el) =>
            el.status?.toLowerCase() !== 'done' &&
            el.status?.toLowerCase() !== 'waived';

          this.disabled =
            processingOfficerChecklistItems.filter(isDoneFilter).length > 0;

          return (
            reviewingOfficerChecklistItems.filter(isDoneFilter).length == 0
          );
        }
      },
      {
        id: 6,
        type: 'guarantor',
        name: 'Guarantor Section Needs Saving',
        description:
          'List of actions to be completed by the processing officer.',
        icon: 'mdi-check-circle-outline',
        isValid: () => {
          return LoanManagementService.guarantorsDidAffordabilityTest(loan);
        }
      },
      {
        id: 3,
        type: 'disbursement',
        name: 'Disbursement Schedule',
        description: 'Allocate fees and loan amount.',
        icon: 'mdi-calendar-clock',
        isValid: () => !this._needDisbursementSchedule
      }
    ];

    return actions.filter((e) => !e.isValid());
  }

  /**
   * Check if all guarantors on the given loan did the affordability test
   *
   * @param { Object } loan - the loan details
   * @returns { Boolean } - returns true if
   */
  static guarantorsDidAffordabilityTest(loan) {
    const types = ['guarantor', 'co-borrower', 'co-maker'];
    const distributions =
      loan?.loanDistributions?.filter((e) => types.includes(e.personType)) ||
      [];

    return (
      distributions.length == 0 ||
      distributions?.filter((e) => !e.affordabilityResults?.incomes?.length)
        .length == 0
    );
  }

  /**
   * Returns a list of outstanding checklist items based on the progress provided
   *
   * @param { Object } loan - the loan details
   * @param { Array<string> } progresses - list of progresses to check for outstanding items
   * @returns {{
   *   totalPledged: number - total pledged on the loan
   *   requiredAmount: number - the amount required to be pledged on the loan
   *   outstandingAmount: number - the outstanding amount to be pledged on the loan
   * }}
   */
  static getOutstandingChecklistItems(loan, progresses) {
    return (
      loan?.systemCheckListItems?.filter((el) =>
        progresses.includes(el.requiredStage?.toLowerCase())
      ) || []
    );
  }

  /**
   * Calculate and return total pledged, required amount and outstanding amount for a life insurance.
   *
   * @param { Object } loan - the loan details
   * @param { Object<{ insuranceFloor: number, freeInsurance: number }> } { insuranceFloor, freeInsurance } - the insurance floor and free insurance settings
   * @returns {{
   *   totalInsurancePledged: number - total pledged on the loan
   *   totalInsuranceRequired: number - the amount required to be pledged on the loan
   *   totalOutstandingInsurance: number - the outstanding amount to be pledged on the loan
   * }}
   */
  static getLifeInsuranceProportions(loan, { insuranceFloor, freeInsurance }) {
    const totalLoanAmount =
      LoanManagementService.calculateTotalLoanAmount(loan);

    const totalInsurancePledged =
      loan?.loanInsurance
        ?.map((el) => {
          el.amountPledgedOnThisLoan =
            el.loanId == loan.id ? +el.pledgedAmount : 0;
          return el.amountPledgedOnThisLoan;
        })
        ?.reduce((acc, cur) => (acc += cur), 0) || 0;

    const totalInsuranceRequired =
      totalLoanAmount - freeInsurance - totalInsurancePledged;

    return {
      totalInsurancePledged,
      totalInsuranceRequired,
      totalOutstandingInsurance:
        totalInsuranceRequired > insuranceFloor ? totalInsuranceRequired : 0
    };
  }

  /**
   * Calculate and return total cash and non cashed required, total cash and non cashed pledged, and oustanding proportions.
   *
   * @param { Object } loan - the loan details
   * @returns {{
   *   totalCashRequired: number - cash required
   *   totalNonCashRequired: number - noncash required
   *   totalCollateralRequired: number - total collateral required
   *
   *   totalCashPledged: number - the cash amount required to be pledged on the loan
   *   totalNonCashPledged: number - the non cash amount required to be pledged on the loan
   *
   *   totalOutstandingCash: number - the outstanding cash/shares amount to be pledged on the loan
   *   totalOutstandingNonCash: number - the outstanding non cash collateral amount to be pledged on the loan
   *   totalOutstandingCollateral: number - the total outstanding collateral amount to be pledged on the loan
   * }}
   */
  static getCollateralProportions(loan) {
    const totalLoanAmount =
      LoanManagementService.calculateTotalLoanAmount(loan);

    const totalCashRequired = (totalLoanAmount * (+loan?.shares || 0)) / 100;
    const totalNonCashRequired =
      (totalLoanAmount * (+loan?.valueCollateralUsed || 0)) / 100;
    const totalCollateralRequired = totalCashRequired + totalNonCashRequired;

    let totalCashPledged = 0;
    let totalNonCashPledged = 0;

    loan.loanCollaterals
      ?.filter((el) => el.loanId == loan.id)
      ?.forEach((el) => {
        if (el.collateral.collateralType.indexOf('Cash') > -1) {
          totalCashPledged += +el.pledgedAmount;
        } else {
          totalNonCashPledged += +el.pledgedAmount;
        }
      });

    const totalOutstandingCash = totalCashRequired - totalCashPledged;
    const totalOutstandingNonCash = totalNonCashRequired - totalNonCashPledged;

    const totalOutstandingCollateral =
      totalNonCashRequired -
      totalNonCashPledged +
      (totalOutstandingCash < 0 ? totalOutstandingCash : 0);

    return {
      totalCashRequired,
      totalNonCashRequired,
      totalCollateralRequired,

      totalCashPledged,
      totalNonCashPledged,

      totalOutstandingCash,
      totalOutstandingNonCash,
      totalOutstandingCollateral
    };
  }

  /**
   * Return the policy based on the loan, product and member category
   *
   * @param { Object } loan - the loan details object
   * @param { Object | null } loanProduct - the loan product, if not provided it will try and get it from the loan details
   */
  getProductPolicy(loan, loanProduct) {
    const product = loanProduct ?? loan.loanProductDetails;
    loan.loanType = product?.loanTypeDetails?.loanTypeCategory;
    loan.loanProduct = loan.loanProductDetails.loanProductName;

    const motorVehicleAge = loan.vehicleAge;
    const memberCategory = loan.loanCategory;

    let policy =
      product?.productPolicies?.find((el) =>
        el.type
          .trim()
          .toLowerCase()
          .includes(memberCategory?.trim().toLowerCase())
      ) || null;

    const isMotorVehicleCategory =
      product?.loanTypeDetails?.loanTypeCategory === 'Motor Vehicle';

    if (isMotorVehicleCategory) {
      policy =
        product.productAgePolicies.find(
          (el) =>
            +el.age == +motorVehicleAge &&
            el.type
              .toLowerCase()
              .replace('age', '')
              .trim()
              .includes(memberCategory?.trim().toLowerCase())
        ) || policy;
    }

    return policy;
  }

  /**
   * Set the loan terms along with the max terms while maintaining a copy of the originalTerms
   *
   * @param { Object } loan - the loan details object
   * @param { Object | null } loanPolicy - the product policy, if this is null it uses the policy stored on the loan
   */
  setPolicyTerms(loan, loanPolicy) {
    const policy = loanPolicy ?? this.getProductPolicy(loan);
    const { frequencyPeriod } = loan;

    const minFrequencyTenure = PaymentCalculationService.getFrequencyTerm(
      frequencyPeriod,
      policy.minTenure
    );
    const maxFrequencyTenure = PaymentCalculationService.getFrequencyTerm(
      frequencyPeriod,
      policy.maxTenure
    );

    loan.maxTerms = {
      shares: +policy.shares,
      tenure: +policy.maxTenure,
      interest: +policy.minInterest,
      frequencyTenure: maxFrequencyTenure,
      valueCollateralUsed: +policy.valueCollateralUsed,
      loanAmount: +(policy.loanCeiling || loan.loanProductDetails?.loanCeiling)
    };

    const previousAdjustedTerms = loan.previousAdjustedTerms || {};
    const adjustedTerms = loan.adjustedTerms || previousAdjustedTerms;

    const adjustmentStatus = ['adjustments-requested', 'adjustments-applied'];

    //check if adjustments is applied and add it to the max terms
    if (adjustmentStatus.includes(loan.adjustmentStatus)) {
      for (const k in adjustedTerms) {
        loan.maxTerms[k] += +adjustedTerms[k];
      }
    }

    //Normalize terms by rounding interest, shares and ltv to the nearest the tenth
    // and tenure to the nearest whole number
    loan.maxTerms.interest = +loan.maxTerms.interest.toFixed(2);
    loan.maxTerms.shares = +loan.maxTerms.shares.toFixed(2);
    loan.maxTerms.valueCollateralUsed =
      +loan.maxTerms.valueCollateralUsed.toFixed(2);
    loan.maxTerms.tenure = Math.ceil(loan.maxTerms.tenure);
    loan.maxTerms.frequencyTenure = Math.ceil(loan.maxTerms.frequencyTenure);

    loan.originalTerms = {
      ...policy,
      minFrequencyTenure,
      maxFrequencyTenure,
      loanFloor: +(policy.loanFloor || loan.loanProductDetails?.loanFloor),
      loanCeiling: +(policy.loanCeiling || loan.loanProductDetails?.loanCeiling)
    };

    loan.interest = loan.id ? loan.interest : policy.maxInterest;
    loan.shares = loan.id ? loan.shares : policy.shares;
    loan.valueCollateralUsed = loan.id
      ? loan.valueCollateralUsed
      : policy.valueCollateralUsed;

    loan.totalInternalLoanAmount = loan.totalInternalLoanAmount || 0;

    //Apply waiver waiver to maxTerms
    //And overwrite requested terms where waiver was applied
    if (loan.previousTermWaiver) {
      const { previousTermWaiver } = loan;

      for (const k in previousTermWaiver) {
        if (!previousTermWaiver[k]) continue;

        if (['interest', 'shares'].includes(k)) {
          loan.maxTerms[k] -= +Number(previousTermWaiver[k]).toFixed(2);
          continue;
        }

        if (k == 'frequencyTenure') {
          loan.maxTerms[k] =
            loan.maxTerms[k] + +Math.ceil(previousTermWaiver[k]);
        }

        loan.maxTerms[k] =
          loan.maxTerms[k] + +Number(previousTermWaiver[k]).toFixed(2);
      }
    }
  }

  /**
   * Check if the loan product is a motor vehicle policy
   *
   * @param { Object } product - the product details
   * @return { Boolean }
   */
  checkIfMotorVehicleCategory(product) {
    return product?.loanTypeDetails?.loanTypeCategory === 'Motor Vehicle';
  }
}
